import { Controller } from '@hotwired/stimulus';
import { destroy } from '@rails/request.js';
import Platform from '../../../helpers/platform';

export default class extends Controller {
  static targets = [
    'checkbox',
    'content',
    'bulkActions',
    'loadingButton',
    'selectionCount',
    'selectAllCheckbox',
    'placeHolderForBulkSelection',
  ];
  static values = {
    selectedIds: { type: Array, default: [] },
    newPostPath: String,
    lastSelectedIndex: { type: Number, default: -1 },
  };

  connect() {
    this.selectedIdsValue = [];
    this.lastSelectedIndexValue = -1;
    this.shiftKeyActive = false;
    this.setupEventListeners();
    this.addNoSelectClass();
  }

  disconnect() {
    this.removeEventListeners();
    this.removeNoSelectClass();
  }

  // Event Handlers
  setupEventListeners() {
    document.addEventListener(
      'keydown',
      (this.handleKeyDown = this.handleKeyDown.bind(this))
    );
    document.addEventListener(
      'keyup',
      (this.handleKeyUp = this.handleKeyUp.bind(this))
    );

    // Add mousedown event listener to prevent text selection during shift+click
    this.element.addEventListener(
      'mousedown',
      (this.handleMouseDown = this.handleMouseDown.bind(this))
    );
  }

  removeEventListeners() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    this.element.removeEventListener('mousedown', this.handleMouseDown);
  }

  // Prevent text selection when shift key is active
  handleMouseDown(event) {
    if (this.shiftKeyActive) {
      event.preventDefault();
    }
  }

  // Add and remove a class that disables text selection during shift+click
  addNoSelectClass() {
    // Create a style element if it doesn't exist
    if (!document.getElementById('bulk-selection-styles')) {
      const style = document.createElement('style');
      style.id = 'bulk-selection-styles';
      style.textContent = `
        .no-select-active {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
      `;
      document.head.appendChild(style);
    }
  }

  removeNoSelectClass() {
    // Remove the style element when controller disconnects
    const styleElement = document.getElementById('bulk-selection-styles');
    if (styleElement) {
      styleElement.remove();
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Shift') {
      this.shiftKeyActive = true;
      this.element.classList.add('no-select-active');
    }
  }

  handleKeyUp(event) {
    if (event.key === 'Shift') {
      this.shiftKeyActive = false;
      this.element.classList.remove('no-select-active');
    }
  }

  // Content Selection Methods
  toggle(event) {
    this.toggleBulkActionsMobile();

    // Prevent the click from triggering the modal open
    event.stopPropagation();

    // Prevent text selection during shift+click
    if (this.shiftKeyActive) {
      event.preventDefault();
    }

    const contentElement = event.currentTarget.closest('[data-content-id]');
    const contentId = contentElement.dataset.contentId;
    const currentIndex = this.contentTargets.indexOf(contentElement);

    if (
      this.shiftKeyActive &&
      this.lastSelectedIndexValue !== -1 &&
      currentIndex !== -1
    ) {
      this.handleRangeSelection(currentIndex);
    } else {
      this.handleSingleSelection(contentElement, contentId);
    }

    // Save the last selected index for potential range selection
    this.lastSelectedIndexValue = currentIndex;
    this.updateSelectionState();
  }

  handleRangeSelection(currentIndex) {
    // Determine the range of indices to select
    const startIndex = Math.min(this.lastSelectedIndexValue, currentIndex);
    const endIndex = Math.max(this.lastSelectedIndexValue, currentIndex);

    // Select all elements in the range
    for (let i = startIndex; i <= endIndex; i++) {
      const rangeElement = this.contentTargets[i];
      const rangeId = rangeElement.dataset.contentId;

      // Only add if not already selected
      if (!this.selectedIdsValue.includes(rangeId)) {
        this.selectedIdsValue = [...this.selectedIdsValue, rangeId];
      }
    }
  }

  handleSingleSelection(contentElement, contentId) {
    const checkbox = contentElement.querySelector("input[type='checkbox']");

    if (this.selectedIdsValue.includes(contentId)) {
      this.selectedIdsValue = this.selectedIdsValue.filter(
        (id) => id !== contentId
      );
      checkbox.checked = false;
    } else {
      this.selectedIdsValue = [...this.selectedIdsValue, contentId];
      checkbox.checked = true;
    }
  }

  updateSelectionState() {
    // Update UI based on selection state
    this.contentTargets.forEach((content) => {
      const contentId = content.dataset.contentId;
      const checkbox = content.querySelector("input[type='checkbox']");

      if (this.selectedIdsValue.includes(contentId)) {
        // Use ring utility instead of border to avoid layout shifts
        content.classList.add('ring-3', 'ring-primary', 'ring-offset-0');
        checkbox.checked = true;
      } else if (checkbox) {
        content.classList.remove('ring-3', 'ring-primary', 'ring-offset-0');
        checkbox.checked = false;
      }
    });

    // Toggle bulk action UI
    if (this.selectedIdsValue.length > 0) {
      this.bulkActionsTarget.classList.remove('hidden');

      // Update selection count
      if (this.hasSelectionCountTarget) {
        this.selectionCountTarget.textContent = this.selectedIdsValue.length;
      }
    } else {
      this.bulkActionsTarget.classList.add('hidden');
    }
  }

  toggleSelectAll(event) {
    const isChecked = this.selectAllCheckboxTarget.checked;

    if (isChecked) {
      // Select all items
      this.selectedIdsValue = this.contentTargets.map(
        (content) => content.dataset.contentId
      );
      this.toggleBulkActionsMobile();

      // Check all checkboxes
      this.contentTargets.forEach((content) => {
        const checkbox = content.querySelector("input[type='checkbox']");
        if (checkbox) {
          checkbox.checked = true;
        }
        // Add selection styling
        content.classList.add('ring-3', 'ring-primary', 'ring-offset-0');
      });
    } else {
      // Deselect all items
      this.selectedIdsValue = [];

      // Uncheck all checkboxes
      this.contentTargets.forEach((content) => {
        const checkbox = content.querySelector("input[type='checkbox']");
        if (checkbox) {
          checkbox.checked = false;
        }
        // Remove selection styling
        content.classList.remove('ring-3', 'ring-primary', 'ring-offset-0');
      });
    }

    // Reset last selected index
    this.lastSelectedIndexValue = -1;

    // Update UI to show/hide bulk actions
    this.updateSelectionState();
  }

  clearSelection() {
    this.selectAllCheckboxTarget.checked = false;
    this.selectedIdsValue = [];
    this.lastSelectedIndexValue = -1;
    this.updateSelectionState();
  }

  // Action Methods
  createPostWithSelected(event) {
    if (this.selectedIdsValue.length === 0) return;

    // Get the base path from the button's data attribute
    const basePath =
      event.currentTarget.dataset.creatorContentBulkSelectionNewPostPath;

    // Create a comma-separated list of content IDs
    const contentIds = this.selectedIdsValue.join(',');

    // Add the content_ids parameter to the path
    const url = `${basePath}?content_ids=${contentIds}`;

    // Redirect to the new post page with content_ids parameter
    window.location.href = url;
  }

  createBulkMessageRequestWithSelected(event) {
    if (this.selectedIdsValue.length === 0) return;

    const basePath =
      event.currentTarget.dataset
        .creatorContentBulkSelectionNewBulkMessageRequestPath;
    const contentIds = this.selectedIdsValue.join(',');
    const url = `${basePath}?content_ids=${contentIds}`;

    window.location.href = url;
  }

  confirmDelete(event) {
    if (this.selectedIdsValue.length === 0) return;

    const confirmed = confirm(
      `Are you sure you want to delete ${this.selectedIdsValue.length} selected item(s)?`
    );

    if (confirmed) {
      // Get the button spinner controller
      const buttonSpinnerController =
        this.application.getControllerForElementAndIdentifier(
          event.currentTarget,
          'button-spinner'
        );

      // Show the spinner
      if (buttonSpinnerController) {
        buttonSpinnerController.showSpinner();
      }

      // Call the delete method
      this.deleteSelected();
    }
  }

  async deleteSelected() {
    if (this.selectedIdsValue.length === 0) return;

    try {
      // Use the correct format for Rails to recognize the method override
      const response = await destroy('/creator/contents/bulk_delete', {
        body: new FormData(),
        query: {
          content_ids: this.selectedIdsValue,
        },
        responseKind: 'turbo-stream',
      });

      if (response.ok) {
        this.clearSelection();
      } else {
        console.error('Failed to delete selected contents');
      }
    } catch (error) {
      console.error('Error deleting contents:', error);
    }
  }

  toggleBulkActionsMobile() {
    if (Platform.useMobileLayout()) {
      const placeholderDiv = this.placeHolderForBulkSelectionTarget;
      if (placeholderDiv && this.hasBulkActionsTarget) {
        placeholderDiv.appendChild(this.bulkActionsTarget);
      }
    }
  }
}
